import {Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject, PLATFORM_ID, afterNextRender} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {SeoService} from '@/app/core/seo/seo.service';
import {JsonLdService} from '@/app/core/seo/json-ld.service';
import {buildBreadcrumbJsonLd} from '@/app/core/seo/article.jsonld';
import {GalleryService} from '@services/gallery.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {environment} from '@/environments/environment';
import {Gallery, GalleryPhoto} from '@/app/core/models/gallery';
import {LocalizedDatePipe} from '@/app/shared/pipes/localizedDate.pipe';
import {PageSelectorComponent} from '@/app/shared/components/page-selector/page-selector.component';
import {TranslateDynamicService} from '@services/translate-dynamic.service';

@Component({
	selector: 'app-gallery-details',
	imports: [
		LocalizedDatePipe,
		PageSelectorComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './gallery-details.component.html',
})
export class GalleryDetailsComponent implements OnInit, OnDestroy {

	private static readonly JSON_LD_BREADCRUMB = 'gallery-breadcrumb';

	private readonly seo = inject(SeoService);
	private readonly jsonLd = inject(JsonLdService);
	private readonly translate = inject(TranslateService);
	private readonly platformId = inject(PLATFORM_ID);

	constructor(
		private readonly galleryService: GalleryService,
		private readonly activatedRoute: ActivatedRoute,
		private readonly router: Router,
		private readonly titleService: Title,
		protected readonly translateDynamicService: TranslateDynamicService,
	) {
		// window n'existe pas pendant le rendu serveur.
		afterNextRender(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
	}

	minioBaseUrl = environment.minioBaseUrl;
	galleryId: string | null = null;
	gallery: Gallery | null = null;
	isShowingModal: boolean = false;
	currentPhoto: GalleryPhoto | null = null;
	photosPerPage  = 20;
	displayedPhotos: GalleryPhoto[] = [];

	page: number = 0;
	totalPages: number = 0;

	onPageChange(newPage: number) {
		this.page = newPage;

		const startIndex = this.page * this.photosPerPage ;
		const endIndex = startIndex + this.photosPerPage ;

		this.displayedPhotos = this.gallery!.photos.slice(startIndex, endIndex);
	}

	ngOnInit() {
		this.activatedRoute.paramMap.subscribe(paramMap => {
			this.galleryId = paramMap.get('galleryId');
			this.loadGallery()
		})
	}

	ngOnDestroy(): void {
		this.jsonLd.remove(GalleryDetailsComponent.JSON_LD_BREADCRUMB);
	}

	loadGallery() {
		if (this.galleryId) {
			this.galleryService.getGallery(this.galleryId).subscribe({
				next: (gallery) => {
					this.gallery = gallery;
					this.titleService.setTitle('Team Peps - ' + gallery.translations[this.translateDynamicService.getLang()].eventName);
					this.publishSeo(gallery);
					this.totalPages = Math.ceil(gallery.photos.length / this.photosPerPage );
					this.displayedPhotos = gallery.photos.slice(0, this.photosPerPage );
				},
				error: () => {
					this.router.navigate(['/not-found']);
				}
			});
		}
	}

	onImageLoad(event: Event) {
		const img = event.target as HTMLImageElement;
		img.classList.remove('opacity-0');
		img.classList.add('opacity-100');
	}

	openPhoto(photo: GalleryPhoto) {
		this.currentPhoto = photo;
		this.toggleOpenPhoto();
	}

	toggleOpenPhoto() {
		this.isShowingModal = !this.isShowingModal;
		if (this.isShowingModal) {
			if (isPlatformBrowser(this.platformId)) document.body.style.overflow = 'hidden';
		} else {
			if (isPlatformBrowser(this.platformId)) document.body.style.overflow = 'auto';
		}
	}

	closeModal() {
		this.currentPhoto = null;
		this.toggleOpenPhoto();
	}

	prevPhoto(){
		if (this.gallery && this.currentPhoto) {
			const currentIndex = this.gallery.photos.findIndex(photo => photo.id === this.currentPhoto?.id);
			if (currentIndex > 0) {
				this.currentPhoto = this.gallery.photos[currentIndex - 1];
			} else {
				this.currentPhoto = this.gallery.photos[this.gallery.photos.length - 1];
			}
		}

	}

	nextPhoto(){
		if (this.gallery && this.currentPhoto) {
			const currentIndex = this.gallery.photos.findIndex(photo => photo.id === this.currentPhoto?.id);
			if (currentIndex < this.gallery.photos.length - 1) {
				this.currentPhoto = this.gallery.photos[currentIndex + 1];
			} else {
				this.currentPhoto = this.gallery.photos[0];
			}
		}
	}

	/** Balises et fil d'Ariane de la galerie. */
	private publishSeo(gallery: Gallery): void {
		const lang = this.translateDynamicService.getLang();
		const translation = gallery.translations?.[lang];
		const path = `/gallery/${gallery.id}`;
		const description = translation?.description?.trim()
			|| this.translate.instant('page.galleries.seo-description');

		this.seo.setPage({
			title: translation?.eventName ?? 'Galerie',
			description,
			path,
			image: this.seo.mediaUrl(gallery.photos?.[0]?.imageKey),
		});

		this.jsonLd.set(GalleryDetailsComponent.JSON_LD_BREADCRUMB, buildBreadcrumbJsonLd([
			{ name: 'Team Peps', url: this.seo.absoluteUrl('/') },
			{ name: this.translate.instant('common.gallery'), url: this.seo.absoluteUrl('/gallery') },
			{ name: translation?.eventName ?? '', url: this.seo.absoluteUrl(path) },
		]));
	}
}
